import React from 'react';
import { Box, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { getFilledButtonText } from '../../theme/buttonContrast';

/**
 * Light segmented control, matching the tab group of the top bar.
 * `items` is a list of `{ key, label, icon }`.
 */
const SegmentedTabs = ({ items, value, onChange, isPos, sx }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const businessData = useSelector((state) => state.core.businessData);
  const activeColor =
    businessData?.secColor || theme.palette.secondary.main;

  if (!items?.length) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: '3px',
        gap: '1px',
        boxSizing: 'border-box',
        flexShrink: 0,
        height: isPos ? '42px' : '34px',
        borderRadius: '8px',
        background: isDarkMode
          ? 'rgba(255, 255, 255, 0.06)'
          : 'rgba(255, 255, 255, 0.65)',
        border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.07)'}`,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        ...sx,
      }}
    >
      {items.map((item) => {
        const active = item.key === value;
        const labelColor = active
          ? isDarkMode
            ? 'rgba(255, 255, 255)'
            : getFilledButtonText(activeColor)
          : isDarkMode
            ? 'rgba(255, 255, 255, 0.5)'
            : 'rgba(0, 0, 0, 0.5)';
        const hoverColor = isDarkMode
          ? 'rgba(255, 255, 255, 0.8)'
          : active
            ? labelColor
            : 'rgba(0, 0, 0, 0.7)';

        return (
          <Button
            key={item.key}
            onClick={() => onChange(item.key)}
            startIcon={item.icon}
            sx={{
              textTransform: 'none',
              whiteSpace: 'nowrap',
              fontSize: isPos ? '13px' : '11.5px',
              fontWeight: active ? 600 : 400,
              letterSpacing: '-0.01em',
              borderRadius: '6px',
              px: '10px',
              py: 0,
              minWidth: 'auto',
              height: isPos ? '34px' : '28px',
              minHeight: isPos ? '34px' : '28px',
              border: 'none',
              color: labelColor,
              backgroundColor: active
                ? isDarkMode
                  ? 'rgba(255, 255, 255, 0.1)'
                  : activeColor
                : 'transparent',
              boxShadow: active
                ? isDarkMode
                  ? '0 1px 2px rgba(0, 0, 0, 0.2)'
                  : '0 1px 3px rgba(0, 0, 0, 0.06), 0 0 0 0.5px rgba(0, 0, 0, 0.04)'
                : 'none',
              transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
              '& .MuiButton-startIcon': {
                mr: '5px',
                ml: 0,
                '& > *:first-of-type': { fontSize: isPos ? 16 : 14 },
              },
              '&:hover': {
                color: hoverColor,
                backgroundColor: active
                  ? isDarkMode
                    ? 'rgba(255, 255, 255, 0.12)'
                    : activeColor
                  : isDarkMode
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.03)',
              },
            }}
          >
            {item.label}
          </Button>
        );
      })}
    </Box>
  );
};

export default SegmentedTabs;
